import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Mail, Clock, Navigation, ArrowRight } from 'lucide-react';
import { SITE } from '../data/site';
import { SectionHead, Reveal } from '../components/Bits';
import { Img } from '../components/Img';

const TODAY = new Date().toLocaleDateString('en-US', { weekday: 'long' });

const VISIT_FAQS = [
  { q: 'Are you open now?', a: 'Yes, we\'re open! Biryanify operates Mon–Thu & Sun from 11 AM to 10 PM, and Fri–Sat from 11 AM to 11 PM. Check our hours above to confirm.' },
  { q: 'Do you have vegetarian options?', a: 'Absolutely. Our menu includes veg biryani, dosa, idli, paneer curries, vegetable dishes and more — all clearly marked on the menu.' },
  { q: 'Do you deliver to nearby areas?', a: 'We operate as a pickup restaurant at our Irving kitchen. Check online ordering to see if your address is in our delivery radius.' },
  { q: 'What is dum biryani?', a: 'Dum biryani is a traditional Hyderabadi cooking method where basmati rice and marinated meat are layered and sealed under a tight lid, slow-steamed so every grain absorbs the meat\'s juices and aromatics.' },
  { q: 'Do you offer catering?', a: 'For catering inquiries, please call us at (214) 377-8283 or email hello@biryanify.com.' },
];

export default function Visit() {
  return (
    <div style={{ background: 'var(--color-surface)' }}>
      <Helmet>
        <title>Visit Biryanify – Hours, Location & Directions | Irving TX</title>
        <meta name="description" content="Biryanify is located at 9400 N MacArthur Blvd in Irving, TX (Valley Ranch/Las Colinas). Open Mon–Thu & Sun 11 AM–10 PM, Fri–Sat 11 AM–11 PM. Get directions and call ahead." />
        <meta property="og:title" content="Visit Biryanify – Hours, Location & Directions | Irving TX" />
        <meta property="og:description" content="Biryanify is located at 9400 N MacArthur Blvd in Irving, TX (Valley Ranch/Las Colinas). Open Mon–Thu & Sun 11 AM–10 PM, Fri–Sat 11 AM–11 PM." />
        <meta property="og:image" content="https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?auto=format&fit=crop&w=1200&q=80" />
        <meta property="og:url" content="https://biryaniify.us/visit" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Visit Biryanify – Hours, Location & Directions | Irving TX" />
        <meta name="twitter:description" content="Biryanify is located at 9400 N MacArthur Blvd in Irving, TX (Valley Ranch/Las Colinas). Open Mon–Thu & Sun 11 AM–10 PM, Fri–Sat 11 AM–11 PM." />
        <meta name="twitter:image" content="https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?auto=format&fit=crop&w=1200&q=80" />
        <link rel="canonical" href="https://biryaniify.us/visit" />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "Are you open now?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Yes, we're open! Biryanify operates Mon–Thu & Sun from 11 AM to 10 PM, and Fri–Sat from 11 AM to 11 PM. Check our hours above to confirm."
              }
            },
            {
              "@type": "Question",
              "name": "Do you have vegetarian options?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Absolutely. Our menu includes veg biryani, dosa, idli, paneer curries, vegetable dishes and more — all clearly marked on the menu."
              }
            },
            {
              "@type": "Question",
              "name": "Do you deliver to nearby areas?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "We operate as a pickup restaurant at our Irving kitchen. Check online ordering to see if your address is in our delivery radius."
              }
            },
            {
              "@type": "Question",
              "name": "What is dum biryani?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "Dum biryani is a traditional Hyderabadi cooking method where basmati rice and marinated meat are layered and sealed under a tight lid, slow-steamed so every grain absorbs the meat's juices and aromatics."
              }
            },
            {
              "@type": "Question",
              "name": "Do you offer catering?",
              "acceptedAnswer": {
                "@type": "Answer",
                "text": "For catering inquiries, please call us at (214) 377-8283 or email hello@biryanify.com."
              }
            }
          ]
        }) }} />
      </Helmet>
      <h1 className="sr-only">Visit Biryanify – Hours, Location & Contact</h1>
      {/* Hero */}
      <section className="relative min-h-[46vh] md:min-h-[52vh] flex flex-col justify-end overflow-hidden">
        <Img src="https://images.unsplash.com/photo-1563379091339-03b21ab4a4f8?auto=format&fit=crop&w=1200&q=80" alt="A plate of fragrant dum biryani" eager className="absolute inset-0" imgClassName="scale-105" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(20,13,8,0.55) 0%, rgba(20,13,8,0.25) 45%, rgba(20,13,8,0.9) 100%)' }} />
        <div className="wrap relative pb-12 pt-32 md:pb-14">
          <p className="eyebrow" style={{ color: 'var(--color-gold)' }}>Visit Us</p>
          <h1 className="font-display mt-3 text-white" style={{ fontSize: 'clamp(2.6rem, 6.5vw, 4.4rem)', lineHeight: 1, textShadow: '0 2px 24px rgba(0,0,0,0.4)' }}>
            Come sit with us.
          </h1>
          <p className="mt-4 max-w-xl" style={{ fontSize: '1.1rem', lineHeight: 1.6, color: 'rgba(255,255,255,0.9)' }}>
            We’re on N MacArthur Blvd in Irving — dine in, pick up, or order for delivery.
          </p>
        </div>
      </section>

      <section className="wrap py-14 md:py-20">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr]">
          {/* Left: contact + hours */}
          <Reveal className="space-y-6">
            <div className="card p-7 md:p-8">
              <SectionHead kicker="Find Us" title="Location & contact" />
              <div className="mt-6 space-y-5">
                <a href={SITE.address.mapsUrl} target="_blank" rel="noreferrer" className="flex items-start gap-4 group">
                  <span className="grid place-items-center w-11 h-11 rounded-2xl shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}><MapPin size={20} /></span>
                  <span>
                    <span className="block font-semibold text-ink group-hover:text-accent-deep transition-colors">{SITE.address.line1}</span>
                    <span className="block text-ink-2">{SITE.address.line2}</span>
                  </span>
                </a>
                <a href={SITE.phoneHref} className="flex items-center gap-4 group">
                  <span className="grid place-items-center w-11 h-11 rounded-2xl shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}><Phone size={20} /></span>
                  <span className="font-semibold text-ink group-hover:text-accent-deep transition-colors">{SITE.phone}</span>
                </a>
                <a href={SITE.emailHref} className="flex items-center gap-4 group">
                  <span className="grid place-items-center w-11 h-11 rounded-2xl shrink-0" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}><Mail size={20} /></span>
                  <span className="font-semibold text-ink group-hover:text-accent-deep transition-colors break-all">{SITE.email}</span>
                </a>
              </div>
              <a href={SITE.address.mapsUrl} target="_blank" rel="noreferrer" className="btn btn-primary mt-7">
                <Navigation size={16} /> Get directions
              </a>
            </div>

            <div className="card p-7 md:p-8">
              <div className="flex items-center gap-3">
                <Clock size={20} style={{ color: 'var(--color-accent)' }} />
                <h3 className="font-display text-xl text-ink">Hours</h3>
              </div>
              <ul className="mt-5">
                {SITE.hours.map((h) => {
                  const today = h.day === TODAY;
                  return (
                    <li
                      key={h.day}
                      className="flex items-center justify-between py-2.5"
                      style={{ borderBottom: '1px solid var(--color-line)' }}
                    >
                      <span className={today ? 'font-semibold text-ink' : 'text-ink-2'}>
                        {h.day}
                        {today && <span className="ml-2 text-[0.68rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded" style={{ background: 'var(--color-accent-soft)', color: 'var(--color-accent-deep)' }}>Today</span>}
                      </span>
                      <span className={`tnum ${today ? 'font-semibold text-ink' : 'text-ink-2'}`}>{h.time}</span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-4 text-[0.82rem] text-muted">Holiday hours may vary — please call ahead to confirm.</p>
            </div>
          </Reveal>

          {/* Right: map + order */}
          <Reveal delay={120} className="space-y-6">
            <a
              href={SITE.address.mapsUrl}
              target="_blank"
              rel="noreferrer"
              className="card block overflow-hidden group"
              aria-label="Open in Google Maps"
            >
              <div className="relative aspect-[4/3] grain" style={{ background: 'linear-gradient(160deg, #26201a 0%, #48381f 100%)' }}>
                {/* stylized map pin */}
                <div className="absolute inset-0 grid place-items-center">
                  <div className="relative">
                    <span className="absolute inset-0 rounded-full animate-ping" style={{ background: 'var(--color-accent)', opacity: 0.35 }} />
                    <span className="relative grid place-items-center w-16 h-16 rounded-full" style={{ background: 'var(--color-accent)', color: 'var(--color-on-accent)', boxShadow: 'var(--pe-shadow-lg)' }}>
                      <MapPin size={28} />
                    </span>
                  </div>
                </div>
                <div className="absolute left-5 bottom-5 rounded-xl px-4 py-2.5" style={{ background: 'color-mix(in srgb, var(--color-ink) 78%, transparent)', color: 'var(--color-ink-inverse)', backdropFilter: 'blur(4px)' }}>
                  <div className="font-display">{SITE.name}</div>
                  <div className="text-[0.8rem] opacity-80 inline-flex items-center gap-1.5">Open in Google Maps <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" /></div>
                </div>
              </div>
            </a>

            <div className="card p-7 md:p-8">
              <SectionHead kicker="See What's Cooking" title="Browse the full menu" />
              <p className="mt-4 text-ink-2" style={{ lineHeight: 1.6 }}>
                Explore our biryani, mandi, tiffins and more before you stop by.
              </p>
              <Link to="/menu" className="btn btn-primary mt-6">
                View the menu <ArrowRight size={16} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
